import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const P = (id: number, w = 1200, h = 700) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}&h=${h}&dpr=1`;

const INDUSTRIES = [
  {
    id: 'government',
    icon: '🏛️',
    title: 'Government & Public Sector',
    tagline: 'Modernize citizen services with AI',
    color: '#0f4c81',
    img: P(2977464),
    overview: "Nepal's National AI Policy 2082 mandates AI integration in public services. Uttar Labs delivers ready-to-deploy solutions that comply with government data standards and integrate with existing e-governance infrastructure.",
    solutions: [
      { title: 'Citizen Service Chatbot', desc: 'Deploy a 24/7 Nepali-language chatbot on government portals to answer queries about documents, procedures, fees, and deadlines — reducing frontline staff load.' },
      { title: 'Document Digitization', desc: 'Batch-process legacy paper records — land cadastres, birth certificates, court filings — into searchable, structured digital archives with full OCR accuracy.' },
      { title: 'Grievance NLP Analysis', desc: 'Automatically classify and route citizen complaints to the right department, with sentiment scoring and urgency flagging for priority handling.' },
      { title: 'Smart Governance Dashboard', desc: 'NLP-powered monitoring of news and social media for public sentiment on government services, policies, and officials — updated daily.' },
      { title: 'Policy & Regulation Q&A (Agentic RAG)', desc: 'Citizens and officers ask questions in plain Nepali — an AI agent retrieves answers across all relevant acts, circulars, and guidelines, with source citations, not hallucinations.' },
      { title: 'Agentic Application Processing', desc: 'Autonomous agents handle the full lifecycle: receive applications, verify documents, check eligibility rules, route for approval, and notify citizens — no manual queue management.' },
    ],
    agencies: ['Ministry of Home Affairs', 'Land Management', 'Local Municipalities', 'Judiciary & Courts', 'Revenue Department'],
  },
  {
    id: 'finance',
    icon: '🏦',
    title: 'Banking & Finance',
    tagline: 'AI-driven inclusion and efficiency',
    color: '#0891b2',
    img: '/news-images/nepal-rastra-bank.jpg',
    overview: "Nepal's banking sector serves millions of underbanked citizens. AI can extend credit access to rural populations, automate KYC compliance, and deliver 24/7 Nepali-language customer service at a fraction of the cost of human agents.",
    solutions: [
      { title: 'Credit Scoring for Unbanked', desc: 'Alternative credit models using non-traditional data to extend loans to rural borrowers without formal credit history — expanding financial inclusion.' },
      { title: 'Nepali Customer Service Bot', desc: 'Handle balance inquiries, loan status, branch/ATM locator, and common banking FAQs in natural Nepali — available 24/7 at zero marginal cost.' },
      { title: 'KYC Document Processing', desc: 'Automatically extract and validate data from citizenship certificates, passports, and utility bills for KYC compliance — cutting processing time from days to minutes.' },
      { title: 'Fraud Detection NLP', desc: "Analyze transaction descriptions and customer communications to flag anomalies and fraud patterns — trained on Nepal's specific financial crime signals." },
      { title: 'Loan Processing Agent (Agentic AI)', desc: 'An autonomous agent that collects required documents, cross-checks them against eligibility criteria, computes a risk score, and routes the file to the right officer — reducing processing from weeks to hours.' },
    ],
    agencies: ['Commercial Banks', 'Development Banks', 'Cooperatives', 'Insurance Companies', 'Microfinance Institutions'],
  },
  {
    id: 'healthcare',
    icon: '🏥',
    title: 'Healthcare',
    tagline: 'Reaching every Nepali with quality care',
    color: '#0f4c81',
    img: P(31499386),
    overview: "Nepal's healthcare system faces massive geographic and resource constraints. AI can extend the reach of healthcare professionals through telemedicine support, automated record-keeping, and multilingual patient communication.",
    solutions: [
      { title: 'Patient Record Digitization', desc: 'OCR and structured extraction from handwritten Nepali medical records — enabling search, analysis, and EHR integration across facilities.' },
      { title: 'Telemedicine NLP Support', desc: 'AI-assisted symptom triage and clinical note generation to help doctors serve more patients via telemedicine with fewer administrative interruptions.' },
      { title: 'Health Advisory Chatbot', desc: 'Nepali-language health information chatbot for primary care guidance, medication reminders, and appointment booking — accessible on basic phones.' },
      { title: 'Medical Document Translation', desc: 'Accurate translation of medical reports and referrals between Nepali and English for specialist consultations and insurance processing.' },
    ],
    agencies: ['Government Hospitals', 'Community Health Centers', 'Health Insurance Board', 'NGO Health Programs', 'Telemedicine Platforms'],
  },
  {
    id: 'agriculture',
    icon: '🌾',
    title: 'Agriculture',
    tagline: "Data-driven farming for Nepal's backbone",
    color: '#0891b2',
    img: P(6713402),
    overview: "Agriculture is Nepal's largest economic sector. AI can help farmers access better market information, detect crop diseases early, and receive advisory services in their own language — without requiring smartphones or technical literacy.",
    solutions: [
      { title: 'Farmer Advisory Chatbot', desc: 'Voice and text-based advisory in Nepali covering crop selection, pest management, and weather guidance — accessible via SMS on basic mobile phones.' },
      { title: 'Market Price Intelligence', desc: 'Scrape and aggregate commodity price data from multiple sources, delivering real-time prices to farmers via SMS or chatbot for better selling decisions.' },
      { title: 'Crop Disease Detection', desc: 'Farmers describe symptoms or upload a photo — AI identifies the disease and recommends treatment in plain Nepali, with follow-up via IVR.' },
      { title: 'Extension Service Automation', desc: 'Help agricultural extension officers scale their impact by automating report drafting, farmer data collection, and bulk communications.' },
    ],
    agencies: ['Ministry of Agriculture', 'Agricultural Development Bank', "Farmers' Cooperatives", 'NGO Agri Programs', 'Commodity Boards'],
  },
  {
    id: 'education',
    icon: '🎓',
    title: 'Education',
    tagline: 'Personalizing learning for every Nepali student',
    color: '#0f4c81',
    img: P(10638210),
    overview: "Nepal's education system spans diverse geographies and 123 languages. AI can support teachers, automate administrative tasks, and create personalized learning experiences that reach students in the most remote areas.",
    solutions: [
      { title: 'Nepali Learning Content AI', desc: 'Generate, adapt, and translate educational content into Nepali and local languages — expanding curriculum reach to minority-language communities.' },
      { title: 'Automated Assessment', desc: 'AI grading and feedback for written Nepali responses — reducing teacher workload while providing instant, detailed feedback to students.' },
      { title: 'Student Support Chatbot', desc: 'Answer student questions about admissions, syllabi, schedules, and scholarships in Nepali — available 24/7 on university and school portals.' },
      { title: 'Language Preservation Tools', desc: 'NLP tools to document, digitize, and analyze endangered Nepali dialects for academic research and cultural preservation programs.' },
    ],
    agencies: ['Universities & Colleges', 'Ministry of Education', 'School Management Committees', 'EdTech Platforms', 'Language Research Institutes'],
  },
];

export function SolutionsPage() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('government');

  const industry = INDUSTRIES.find(i => i.id === activeId)!;

  return (
    <div className="tab-content">
      <section className="hero-section">
        <h1 className="hero-title">Industry Solutions</h1>
        <p className="hero-subtitle">
          AI & NLP solutions tailored to the specific needs and constraints of Nepal's key sectors.
        </p>
      </section>

      <section className="section">
        {/* Industry selector */}
        <div className="party-tabs" style={{ marginBottom: '2rem', flexWrap: 'wrap' }}>
          {INDUSTRIES.map(ind => (
            <button
              key={ind.id}
              className={`party-tab ${activeId === ind.id ? 'active' : ''}`}
              onClick={() => setActiveId(ind.id)}
              style={activeId === ind.id ? { color: ind.color, borderBottomColor: ind.color } : {}}
            >
              {ind.icon} {ind.title}
            </button>
          ))}
        </div>

        {/* Industry hero with background image */}
        <div
          className="civic-hero"
          style={{
            padding: '2.5rem 2rem',
            marginBottom: '1.5rem',
            background: `linear-gradient(135deg, ${industry.color}f0 0%, ${industry.color}c0 100%), url(${industry.img})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="civic-hero-inner">
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>{industry.icon}</div>
            <h2 style={{ color: 'white', fontSize: '1.8rem', fontWeight: 700, marginBottom: '0.4rem' }}>{industry.title}</h2>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', fontStyle: 'italic', marginBottom: '1rem' }}>{industry.tagline}</p>
            <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', maxWidth: 700, margin: '0 auto', lineHeight: 1.7 }}>{industry.overview}</p>
          </div>
        </div>

        {/* Solutions grid */}
        <div className="cards-grid">
          {industry.solutions.map(s => (
            <div key={s.title} className="card" style={{ borderTop: `3px solid ${industry.color}` }}>
              <h3 className="card-title" style={{ fontSize: '1.05rem' }}>{s.title}</h3>
              <p className="card-description">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Relevant orgs */}
        <div className="card" style={{ marginTop: '0.5rem' }}>
          <h4 style={{ color: '#1e293b', fontWeight: 700, marginBottom: '0.75rem' }}>Relevant Organizations</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {industry.agencies.map(a => (
              <span
                key={a}
                style={{
                  background: industry.color === '#0891b2' ? 'rgba(8,145,178,0.1)' : 'rgba(15,76,129,0.1)',
                  color: industry.color,
                  padding: '0.3rem 0.875rem',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="card" style={{ background: 'linear-gradient(135deg, #0f4c81 0%, #0891b2 100%)', color: 'white', textAlign: 'center' }}>
          <h2 style={{ color: 'white', marginBottom: '0.75rem' }}>Let's build your AI solution</h2>
          <p style={{ opacity: 0.9, marginBottom: '1.5rem', maxWidth: 500, margin: '0 auto 1.5rem' }}>
            Tell us about your organization's challenges. We'll design a custom AI solution in 30 minutes.
          </p>
          <button
            onClick={() => navigate('/contact')}
            style={{ padding: '0.875rem 2rem', background: 'white', color: '#0f4c81', border: 'none', borderRadius: '25px', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }}
          >
            Get a Free Consultation
          </button>
        </div>
      </section>
    </div>
  );
}
